/**
 * Geoeconomic AI Intelligence API Service
 * Connects to the Claude-powered geoeconomic intelligence backend.
 *
 * NOTE: Several of these endpoints (run-pipeline, enrich-existing, generate-briefing,
 * scheduler controls) require a superadmin Bearer token on the backend, so every
 * request below attaches the auth token from local storage when one is present.
 */

import { getAuthTokenFromStorage } from '../utils/authStorage';

import { API_BASE_URL as API_BASE } from './apiConfig';

const buildHeaders = (extra = {}) => {
  const headers = { ...extra };
  const token = getAuthTokenFromStorage();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

const safeJson = async (res) => {
  try {
    return await res.json();
  } catch (_) {
    return null;
  }
};

const handleError = async (res, fallback) => {
  const body = await safeJson(res);
  const detail = body?.detail || body?.message || fallback || `HTTP ${res.status}`;
  return { success: false, status: res.status, error: detail };
};

export const getAiStatus = async () => {
  try {
    const res = await fetch(`${API_BASE}/api/geoeconomic-ai/status`, {
      headers: buildHeaders(),
    });
    if (!res.ok) {
      const err = await handleError(res, 'Status check failed');
      return { available: false, ...err };
    }
    return await res.json();
  } catch (err) {
    console.error('AI status check failed:', err);
    return { success: false, available: false, error: err.message };
  }
};

export const runFullPipeline = async ({ useNewsapi = true, useExisting = true, maxEnrich = 15, daysBack = 7 } = {}) => {
  try {
    const params = new URLSearchParams({
      use_newsapi: useNewsapi,
      use_existing: useExisting,
      max_enrich: maxEnrich,
      days_back: daysBack,
    });
    const res = await fetch(`${API_BASE}/api/geoeconomic-ai/run-pipeline?${params}`, {
      method: 'POST',
      headers: buildHeaders(),
    });
    if (!res.ok) return await handleError(res, 'Pipeline run failed');
    return await res.json();
  } catch (err) {
    console.error('Pipeline run failed:', err);
    return { success: false, error: err.message };
  }
};

export const enrichExistingArticles = async (limit = 10, daysBack = 14) => {
  try {
    const params = new URLSearchParams({ limit, days_back: daysBack });
    const res = await fetch(`${API_BASE}/api/geoeconomic-ai/enrich-existing?${params}`, {
      method: 'POST',
      headers: buildHeaders(),
    });
    if (!res.ok) return await handleError(res, 'Enrich failed');
    return await res.json();
  } catch (err) {
    console.error('Enrich failed:', err);
    return { success: false, error: err.message };
  }
};

export const getEnrichedEvents = async (limit = 20) => {
  try {
    const res = await fetch(`${API_BASE}/api/geoeconomic-ai/enriched-events?limit=${limit}`, {
      headers: buildHeaders(),
    });
    if (!res.ok) {
      const err = await handleError(res, 'Fetch enriched events failed');
      return { ...err, events: [] };
    }
    return await res.json();
  } catch (err) {
    console.error('Fetch enriched events failed:', err);
    return { success: false, events: [], error: err.message };
  }
};

export const getLatestBriefing = async () => {
  try {
    const res = await fetch(`${API_BASE}/api/geoeconomic-ai/briefing`, {
      headers: buildHeaders(),
    });
    if (!res.ok) {
      const err = await handleError(res, 'Fetch briefing failed');
      return { ...err, briefing: null };
    }
    return await res.json();
  } catch (err) {
    console.error('Fetch briefing failed:', err);
    return { success: false, briefing: null, error: err.message };
  }
};

export const generateBriefing = async (period = 'week', daysBack = 7) => {
  try {
    const params = new URLSearchParams({ period, days_back: daysBack });
    const res = await fetch(`${API_BASE}/api/geoeconomic-ai/generate-briefing?${params}`, {
      method: 'POST',
      headers: buildHeaders(),
    });
    if (!res.ok) return await handleError(res, 'Generate briefing failed');
    return await res.json();
  } catch (err) {
    console.error('Generate briefing failed:', err);
    return { success: false, error: err.message };
  }
};

export const queryIntelligence = async (question) => {
  try {
    const res = await fetch(`${API_BASE}/api/geoeconomic-ai/query`, {
      method: 'POST',
      headers: buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ question }),
    });
    if (!res.ok) return await handleError(res, 'Intelligence query failed');
    return await res.json();
  } catch (err) {
    console.error('Intelligence query failed:', err);
    return { success: false, error: err.message };
  }
};

const geoeconomicAiApi = {
  getAiStatus,
  runFullPipeline,
  enrichExistingArticles,
  getEnrichedEvents,
  getLatestBriefing,
  generateBriefing,
  queryIntelligence,
};

export default geoeconomicAiApi;
